import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import HorizonRing from "@/components/HorizonRing";
import LevelUpModal from "@/components/LevelUpModal";
import { BRANCH_META } from "@/lib/branches";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Flame,
    Sparkles,
    ArrowRight,
    MessageCirclePlus,
    Check,
    X,
    Wrench,
    Compass,
    ListChecks,
    Users,
    Send,
    Wand2,
    Rocket,
    Plus,
} from "lucide-react";

const SECTIONS = [
    { key: "microtasks", label: "Microtasks", icon: ListChecks },
    { key: "peers", label: "Peers", icon: Users },
    { key: "projects", label: "Projects", icon: Wrench },
];

export default function MyInterests() {
    const [items, setItems] = useState(null);
    const [microtaskGroups, setMicrotaskGroups] = useState(null);
    const [peers, setPeers] = useState(null);
    const [tab, setTab] = useState("microtasks");

    // Modals
    const [asking, setAsking] = useState(null);
    const [askMsg, setAskMsg] = useState("");
    const [notingPeer, setNotingPeer] = useState(null); // {user_id, name}
    const [noteMsg, setNoteMsg] = useState("");
    const [completing, setCompleting] = useState(null); // {microtask, discipline}
    const [completingNote, setCompletingNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [reward, setReward] = useState(null);
    const [generatingFor, setGeneratingFor] = useState(null);
    const [loadError, setLoadError] = useState(null);

    const load = async () => {
        try {
            setLoadError(null);
            const [interests, mts, ps] = await Promise.all([
                api.get("/my/interests"),
                api.get("/microtasks/me"),
                api.get("/peers/me"),
            ]);
            setItems(interests.data);
            setMicrotaskGroups(mts.data);
            setPeers(ps.data);
        } catch (e) {
            // Fallback to empty arrays so the page renders instead of hanging
            // in a null-return blank state. Show a retry banner up top.
            setItems((prev) => prev ?? []);
            setMicrotaskGroups((prev) => prev ?? []);
            setPeers((prev) => prev ?? []);
            setLoadError(
                e?.response?.data?.detail ||
                "We couldn't load your interests right now. Check your connection and retry."
            );
        }
    };

    useEffect(() => {
        load();
    }, []);

    const submitAsk = async (e) => {
        e.preventDefault();
        if (!askMsg.trim()) return;
        setBusy(true);
        try {
            await api.post("/projects/request-info", {
                project_slug: asking.project_slug,
                message: askMsg,
            });
            setAsking(null);
            setAskMsg("");
            toast.success("Request sent to the project lead.");
            load();
        } finally {
            setBusy(false);
        }
    };

    const submitNote = async (e) => {
        e.preventDefault();
        if (!noteMsg.trim() || !notingPeer) return;
        setBusy(true);
        try {
            await api.post("/notes/send", {
                to_user_id: notingPeer.user_id,
                message: noteMsg,
            });
            toast.success(`Note sent to ${notingPeer.name}.`);
            setNotingPeer(null);
            setNoteMsg("");
        } catch (e) {
            toast.error("Couldn't send that note.");
        } finally {
            setBusy(false);
        }
    };

    const submitComplete = async (e) => {
        e.preventDefault();
        if (!completing) return;
        setBusy(true);
        try {
            const { data } = await api.post("/microtasks/complete", {
                microtask_id: completing.microtask.id,
                note: completingNote,
            });
            setCompleting(null);
            setCompletingNote("");
            if (data.reward) setReward(data.reward);
            toast.success("Microtask logged. Great work.");
            load();
        } catch {
            toast.error("Couldn't record that task.");
        } finally {
            setBusy(false);
        }
    };

    const generateMore = async (slug) => {
        setGeneratingFor(slug);
        try {
            await api.post(`/microtasks/generate/${slug}`);
            toast.success("Fresh tailored tasks added.");
            load();
        } catch {
            toast.error("Couldn't generate tasks right now.");
        } finally {
            setGeneratingFor(null);
        }
    };

    if (!items) {
        return (
            <div className="min-h-screen w-full">
                <TopBar />
                <div className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-8 text-center text-slate-500 font-display">
                    Loading your interests…
                </div>
            </div>
        );
    }

    const totalPending = (microtaskGroups || []).reduce((n, g) => n + g.pending_count, 0);
    const totalCompleted = (microtaskGroups || []).reduce((n, g) => n + g.completed_count, 0);

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <p className="chip" style={{ background: BRANCH_META.sciences.soft, color: BRANCH_META.sciences.text }}>
                    <Flame className="w-3 h-3" /> Your interests
                </p>
                <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none">
                    Every beacon you've ignited.
                </h1>
                <p className="text-slate-600 mt-2 max-w-2xl">
                    Micro-tasks to make your first mark, peers who share your disciplines, and open projects — all in one place.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                    <Link to="/projects" className="arcade-btn" data-variant="ghost" data-testid="interests-browse-projects">
                        <Rocket className="w-4 h-4" /> Browse all projects
                    </Link>
                    <Link to="/projects/new" className="arcade-btn" data-variant="primary" data-testid="interests-post-project">
                        <Plus className="w-4 h-4" /> Post a project
                    </Link>
                </div>

                {loadError && (
                    <div className="mt-6 crystal rounded-3xl p-5" data-testid="interests-load-error">
                        <div className="flex flex-wrap items-start gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="font-display font-bold text-slate-900">
                                    We hit a snag loading this page.
                                </div>
                                <div className="text-sm text-slate-600 mt-1">{loadError}</div>
                            </div>
                            <button
                                onClick={load}
                                className="arcade-btn"
                                data-variant="primary"
                                data-testid="interests-load-retry"
                            >
                                Try again
                            </button>
                        </div>
                        {loadError.toLowerCase().includes("authenticat") && (
                            <div className="mt-3 pt-3 border-t border-slate-200/70 text-sm text-slate-600">
                                Still stuck?{" "}
                                <button
                                    onClick={async () => {
                                        try { await api.post("/auth/logout"); } catch {}
                                        window.location.href = "/";
                                    }}
                                    className="text-humanities font-display font-bold underline underline-offset-2"
                                    data-testid="interests-signin-again"
                                >Sign in again</button>{" "}to reset your session.
                            </div>
                        )}
                    </div>
                )}

                {items.length === 0 && (
                    <div className="mt-10 crystal rounded-3xl p-10 text-center">
                        <Compass className="w-8 h-8 mx-auto text-slate-400" />
                        <h2 className="font-display font-bold text-2xl tracking-tighter text-slate-900 mt-3">
                            No beacons yet.
                        </h2>
                        <p className="text-slate-600 mt-1">
                            Head to the Atlas and light your first one.
                        </p>
                        <Link to="/atlas" className="arcade-btn mt-5 inline-flex" data-variant="primary" data-testid="empty-goto-atlas">
                            Open the Atlas <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                )}

                {items.length > 0 && (
                    <>
                        {/* Tabs */}
                        <div className="mt-8 flex items-center gap-1.5 p-1.5 rounded-full crystal w-fit">
                            {SECTIONS.map((s) => {
                                const Icon = s.icon;
                                const active = tab === s.key;
                                const badge = s.key === "microtasks" ? totalPending : s.key === "peers" ? (peers?.length || 0) : items.reduce((n, it) => n + it.projects.length, 0);
                                return (
                                    <button
                                        key={s.key}
                                        onClick={() => setTab(s.key)}
                                        className={`px-4 py-2 rounded-full text-sm font-display font-bold transition-all inline-flex items-center gap-2 ${active ? "bg-slate-900 text-cream" : "text-slate-700 hover:bg-white/70"}`}
                                        data-testid={`tab-${s.key}`}
                                    >
                                        <Icon className="w-4 h-4" />
                                        {s.label}
                                        {badge > 0 && (
                                            <span
                                                className={`ml-0.5 min-w-[20px] px-1.5 rounded-full text-[10px] font-black tracking-wide ${active ? "bg-cream text-slate-900" : "bg-slate-900 text-cream"}`}
                                            >
                                                {badge}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* MICROTASKS */}
                        {tab === "microtasks" && (
                            <div className="mt-6 space-y-6" data-testid="section-microtasks">
                                <div className="crystal rounded-3xl p-5 flex flex-wrap items-center gap-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-11 h-11 rounded-2xl bg-slate-900 text-cream flex items-center justify-center">
                                            <ListChecks className="w-5 h-5" />
                                        </div>
                                        <div>
                                            <div className="text-xs uppercase tracking-widest font-bold text-slate-400">Your queue</div>
                                            <div className="font-display font-black text-xl text-slate-900 tracking-tight leading-tight">
                                                {totalPending} pending · {totalCompleted} completed
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-sm text-slate-600 ml-auto max-w-xs">
                                        Small, high-signal tasks per discipline. Each one moves the community forward and stamps your resume.
                                    </p>
                                </div>

                                {(microtaskGroups || []).map((g) => {
                                    const meta = BRANCH_META[g.discipline.branch];
                                    return (
                                        <section key={g.discipline.slug} className="crystal rounded-3xl p-6" data-testid={`microtasks-${g.discipline.slug}`}>
                                            <header className="flex items-start gap-3">
                                                <div className="w-2 h-10 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                                                <div className="flex-1 min-w-0">
                                                    <span className="chip" style={{ background: meta.soft, color: meta.text }}>
                                                        {meta.name} · {g.mode}
                                                    </span>
                                                    <h3 className="font-display font-bold text-xl tracking-tighter text-slate-900 mt-1 leading-tight">
                                                        {g.discipline.name}
                                                    </h3>
                                                </div>
                                                <button
                                                    disabled={generatingFor === g.discipline.slug}
                                                    onClick={() => generateMore(g.discipline.slug)}
                                                    className="arcade-btn"
                                                    data-variant="ghost"
                                                    data-testid={`generate-tasks-${g.discipline.slug}`}
                                                    title="Ask AI for more tailored tasks"
                                                >
                                                    <Wand2 className="w-4 h-4" />
                                                    {generatingFor === g.discipline.slug ? "Generating…" : "More tasks"}
                                                </button>
                                            </header>

                                            <ul className="mt-4 space-y-2">
                                                {g.microtasks.map((t) => (
                                                    <li
                                                        key={t.id}
                                                        className={`rounded-2xl p-4 flex items-start gap-3 transition-all ${t.completed ? "bg-slate-50 border border-slate-200" : "bg-white/80 border border-slate-200 hover:border-slate-300"}`}
                                                        data-testid={`microtask-${t.id}`}
                                                    >
                                                        <div
                                                            className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white ${t.completed ? "" : ""}`}
                                                            style={{ background: t.completed ? meta.color : "rgba(148,163,184,0.35)" }}
                                                        >
                                                            {t.completed ? <Check className="w-3.5 h-3.5" /> : <Flame className="w-3.5 h-3.5" />}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className={`font-display font-bold text-slate-900 ${t.completed ? "line-through text-slate-400" : ""}`}>
                                                                    {t.title}
                                                                </span>
                                                                {!t.is_generic && (
                                                                    <span className="chip" style={{ background: BRANCH_META.arts.soft, color: BRANCH_META.arts.text }}>
                                                                        <Wand2 className="w-3 h-3" /> AI-tailored
                                                                    </span>
                                                                )}
                                                                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                                                                    +{t.xp} XP
                                                                </span>
                                                            </div>
                                                            <p className={`text-sm mt-1 ${t.completed ? "text-slate-400" : "text-slate-600"}`}>
                                                                {t.description}
                                                            </p>
                                                        </div>
                                                        {!t.completed && (
                                                            <button
                                                                onClick={() => setCompleting({ microtask: t, discipline: g.discipline })}
                                                                className="arcade-btn flex-shrink-0"
                                                                data-variant={g.discipline.branch}
                                                                data-testid={`complete-${t.id}`}
                                                                style={{ padding: "0.4rem 0.9rem", fontSize: "0.75rem" }}
                                                            >
                                                                <Check className="w-3 h-3" /> Complete
                                                            </button>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    );
                                })}
                            </div>
                        )}

                        {/* PEERS */}
                        {tab === "peers" && (
                            <div className="mt-6 space-y-4" data-testid="section-peers">
                                {(!peers || peers.length === 0) && (
                                    <div className="crystal rounded-3xl p-10 text-center">
                                        <Users className="w-8 h-8 mx-auto text-slate-400" />
                                        <h3 className="font-display font-bold text-xl tracking-tight text-slate-900 mt-3">
                                            No peers here yet.
                                        </h3>
                                        <p className="text-slate-600 mt-1 max-w-md mx-auto">
                                            No one else has lit a beacon in your disciplines. Complete a microtask
                                            to signal interest — when others arrive, they'll appear here first.
                                        </p>
                                    </div>
                                )}
                                {peers && peers.map((p) => (
                                    <div key={p.user_id} className="crystal rounded-2xl p-5 flex items-center gap-4" data-testid={`peer-${p.user_id}`}>
                                        <div
                                            className="w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-white flex-shrink-0"
                                            style={{ background: `linear-gradient(135deg, ${BRANCH_META.arts.color}, ${BRANCH_META.humanities.color})` }}
                                        >
                                            {(p.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-bold text-slate-900">{p.name}</div>
                                            <div className="text-xs text-slate-500">Level {p.level} · {p.xp} XP · {p.shared_disciplines.length} shared</div>
                                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                {p.shared_disciplines.slice(0, 4).map((d) => (
                                                    <span key={d.slug} className="chip text-[10px]" style={{ background: BRANCH_META[d.branch].soft, color: BRANCH_META[d.branch].text, padding: "0.15rem 0.55rem" }}>
                                                        {d.name}
                                                    </span>
                                                ))}
                                                {p.shared_disciplines.length > 4 && (
                                                    <span className="text-[10px] text-slate-400 font-bold">
                                                        +{p.shared_disciplines.length - 4} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-2 flex-shrink-0">
                                            <button
                                                onClick={() => setNotingPeer({ user_id: p.user_id, name: p.name })}
                                                className="arcade-btn"
                                                data-variant="primary"
                                                data-testid={`send-note-${p.user_id}`}
                                                style={{ padding: "0.4rem 0.9rem", fontSize: "0.75rem" }}
                                            >
                                                <Send className="w-3 h-3" /> Send note
                                            </button>
                                            {p.username && (
                                                <Link
                                                    to={`/u/${p.username}`}
                                                    className="text-xs text-slate-500 hover:text-slate-900 font-display font-bold text-center"
                                                    data-testid={`view-profile-${p.user_id}`}
                                                >
                                                    View ledger →
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* PROJECTS (original view) */}
                        {tab === "projects" && (
                            <div className="mt-6 space-y-6" data-testid="section-projects">
                                {items.map((it, idx) => {
                                    const meta = BRANCH_META[it.discipline.branch];
                                    return (
                                        <motion.section
                                            key={it.discipline.slug}
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.04 }}
                                            className="crystal rounded-3xl p-6"
                                            data-testid={`interest-${it.discipline.slug}`}
                                        >
                                            <header className="flex items-start gap-4">
                                                <HorizonRing size={72} stroke={9} pct={it.discipline.progress} color={meta.color} />
                                                <div className="flex-1 min-w-0">
                                                    <span className="chip" style={{ background: meta.soft, color: meta.text }}>
                                                        {meta.name} · {it.mode}
                                                    </span>
                                                    <h2 className="font-display font-bold text-2xl tracking-tighter text-slate-900 mt-1 leading-tight">
                                                        {it.discipline.name}
                                                    </h2>
                                                    <p className="text-sm text-slate-600 mt-0.5">{it.discipline.blurb}</p>
                                                </div>
                                                <Link
                                                    to={`/discipline/${it.discipline.slug}`}
                                                    className="arcade-btn"
                                                    data-variant="ghost"
                                                    data-testid={`open-discipline-${it.discipline.slug}`}
                                                >
                                                    <Wrench className="w-4 h-4" /> Hub
                                                </Link>
                                            </header>

                                            {it.projects.length === 0 ? (
                                                <div className="mt-4 rounded-2xl border border-dashed border-slate-300 bg-white/60 p-4 text-sm text-slate-600">
                                                    No open projects here yet. Complete a microtask in the queue tab to signal you're active — leads will see it.
                                                </div>
                                            ) : (
                                                <div className="mt-5 space-y-3">
                                                    {it.projects.map((p) => (
                                                        <div key={p.slug} className="rounded-2xl border border-slate-200 bg-white/80 p-4" data-testid={`project-${p.slug}`}>
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0">
                                                                    <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: meta.text }}>
                                                                        {p.org}
                                                                    </div>
                                                                    <h3 className="font-display font-bold text-lg text-slate-900 tracking-tight mt-0.5">
                                                                        {p.title}
                                                                    </h3>
                                                                    <p className="text-sm text-slate-600 mt-0.5">{p.summary}</p>
                                                                    <p className="text-xs text-slate-500 mt-1">
                                                                        Lead: <span className="font-bold text-slate-800">{p.lead_name}</span>
                                                                    </p>
                                                                </div>
                                                                <button
                                                                    disabled={p.requested_info}
                                                                    onClick={() => setAsking({ project_slug: p.slug, project_title: p.title })}
                                                                    className="arcade-btn"
                                                                    data-variant={p.requested_info ? "ghost" : it.discipline.branch}
                                                                    data-testid={`request-info-${p.slug}`}
                                                                    style={{ padding: "0.4rem 0.9rem", fontSize: "0.8rem" }}
                                                                >
                                                                    {p.requested_info ? (<><Check className="w-3 h-3" /> Info requested</>) : (<><MessageCirclePlus className="w-3 h-3" /> Request info</>)}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </motion.section>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Complete microtask modal */}
            <AnimatePresence>
                {completing && (
                    <ModalBackdrop onClose={() => setCompleting(null)}>
                        <motion.form onSubmit={submitComplete} onClick={(e) => e.stopPropagation()} initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }} className="crystal rounded-3xl p-6 w-full max-w-md relative">
                            <CloseX onClose={() => setCompleting(null)} />
                            <p className="chip" style={{ background: BRANCH_META[completing.discipline.branch].soft, color: BRANCH_META[completing.discipline.branch].text }}>
                                <ListChecks className="w-3 h-3" /> {completing.microtask.xp} XP
                            </p>
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-2">
                                {completing.microtask.title}
                            </h2>
                            <p className="text-sm text-slate-600 mt-1">{completing.microtask.description}</p>
                            <label className="block text-xs uppercase tracking-widest font-bold text-slate-400 mt-4 mb-1.5">
                                Your note (optional, appears on your ledger)
                            </label>
                            <textarea
                                autoFocus
                                className="field-input min-h-[100px] resize-y"
                                placeholder="I did XYZ..."
                                value={completingNote}
                                onChange={(e) => setCompletingNote(e.target.value)}
                                maxLength={400}
                                data-testid="complete-note-input"
                            />
                            <div className="mt-4 flex justify-end gap-2">
                                <button type="button" onClick={() => setCompleting(null)} className="arcade-btn" data-variant="ghost">Cancel</button>
                                <button className="arcade-btn" data-variant={completing.discipline.branch} disabled={busy} data-testid="complete-microtask-submit">
                                    <Check className="w-4 h-4" /> Mark complete
                                </button>
                            </div>
                        </motion.form>
                    </ModalBackdrop>
                )}
            </AnimatePresence>

            {/* Send note to peer modal */}
            <AnimatePresence>
                {notingPeer && (
                    <ModalBackdrop onClose={() => setNotingPeer(null)}>
                        <motion.form onSubmit={submitNote} onClick={(e) => e.stopPropagation()} initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }} className="crystal rounded-3xl p-6 w-full max-w-md relative">
                            <CloseX onClose={() => setNotingPeer(null)} />
                            <p className="chip" style={{ background: BRANCH_META.humanities.soft, color: BRANCH_META.humanities.text }}>
                                <Send className="w-3 h-3" /> Peer note
                            </p>
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-2">
                                Send a note to {notingPeer.name}
                            </h2>
                            <p className="text-sm text-slate-600 mt-1">
                                They'll see it in their Inbox with your name and a link to your ledger.
                            </p>
                            <textarea
                                autoFocus
                                className="field-input mt-3 min-h-[120px] resize-y"
                                placeholder="Hey — I saw we're both in Marine Biology. Would you want to collab on…"
                                value={noteMsg}
                                onChange={(e) => setNoteMsg(e.target.value)}
                                required
                                maxLength={1000}
                                data-testid="peer-note-input"
                            />
                            <div className="mt-4 flex justify-end gap-2">
                                <button type="button" onClick={() => setNotingPeer(null)} className="arcade-btn" data-variant="ghost">Cancel</button>
                                <button className="arcade-btn" data-variant="primary" disabled={busy || !noteMsg.trim()} data-testid="peer-note-submit">
                                    <Send className="w-4 h-4" /> Send note
                                </button>
                            </div>
                        </motion.form>
                    </ModalBackdrop>
                )}
            </AnimatePresence>

            {/* Ask lead modal (unchanged) */}
            <AnimatePresence>
                {asking && (
                    <ModalBackdrop onClose={() => setAsking(null)}>
                        <motion.form onSubmit={submitAsk} onClick={(e) => e.stopPropagation()} initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }} className="crystal rounded-3xl p-6 w-full max-w-md relative">
                            <CloseX onClose={() => setAsking(null)} />
                            <p className="chip" style={{ background: BRANCH_META.arts.soft, color: BRANCH_META.arts.text }}>
                                <Sparkles className="w-3 h-3" /> Request info
                            </p>
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-2">
                                {asking.project_title}
                            </h2>
                            <p className="text-sm text-slate-600 mt-1">
                                Send a message to the project lead — they'll get your name, your ledger URL and this note.
                            </p>
                            <textarea
                                autoFocus
                                className="field-input mt-3 min-h-[110px] resize-y"
                                placeholder="I'd like to know how many hours per week you'd need from a Contributor…"
                                value={askMsg}
                                onChange={(e) => setAskMsg(e.target.value)}
                                required
                                maxLength={800}
                                data-testid="request-info-message"
                            />
                            <div className="mt-4 flex justify-end gap-2">
                                <button type="button" onClick={() => setAsking(null)} className="arcade-btn" data-variant="ghost">Cancel</button>
                                <button className="arcade-btn" data-variant="primary" disabled={busy || !askMsg.trim()} data-testid="request-info-submit">
                                    <MessageCirclePlus className="w-4 h-4" /> Send request
                                </button>
                            </div>
                        </motion.form>
                    </ModalBackdrop>
                )}
            </AnimatePresence>

            <LevelUpModal reward={reward} onClose={() => setReward(null)} />
        </div>
    );
}

function ModalBackdrop({ children, onClose }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-md flex items-center justify-center p-4"
            onClick={onClose}
        >
            {children}
        </motion.div>
    );
}

function CloseX({ onClose }) {
    return (
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900" data-testid="modal-close">
            <X className="w-5 h-5" />
        </button>
    );
}
